import { InfoPage } from '@/components/info-page';
import { LoadingPage } from '@/components/loading-page';
import { getAnnouncements } from '@/lib/api-calls';
import { paginationRequestSchema } from '@/lib/pagination';
import { validateRouteRole } from '@/lib/routing';
import { usePaginatedFilters } from '@/lib/filtering';
import { formatInstant, isAirAdmin } from '@airreading/common';
import {
  Announcement,
  AnnouncementPlacementLabelMap,
  announcementPlacementSchema,
  AnnouncementStatus,
  AnnouncementStatusLabelMap,
  announcementStatusSchema,
  announcementTypeSchema,
} from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useMemo } from 'react';
import { z } from 'zod';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { AnnouncementTypeBadge } from './-components/annonuncement-type-badge';
import { Button } from '@/components/ui/button';
import { AnnouncementFormModal } from './-components/announcement-form-modal';
import {
  announcementPlacementOptions,
  announcementStatusOptions,
  announcementTypeOptions,
} from '@/lib/announcement-utils';
import { Pencil } from 'lucide-react';
import { Temporal } from 'temporal-polyfill';
import { useTimeZone } from '@/auth/useTimeZone';
import clsx from 'clsx';
import { coachesSectionTabs } from '../-components/tabs';

export const Route = createFileRoute('/_layout/teachers/announcement/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: Page,
  validateSearch: paginationRequestSchema.extend({
    query: z.string().optional(),
    placements: z.array(announcementPlacementSchema).optional(),
    types: z.array(announcementTypeSchema).optional(),
    statuses: z.array(announcementStatusSchema).optional(),
  }),
});

function Page() {
  return (
    <Suspense fallback={<LoadingPage />}>
      <PageDetail />
    </Suspense>
  );
}

function PageDetail() {
  const { timeZone } = useTimeZone();
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[nameQuery, activeNameQuery], setNameQuery],
      placements: [[placementIds, activePlacementIds], setPlacementIds],
      types: [[typeIds, activeTypeIds], setTypeIds],
      statuses: [[statusIds, activeStatusIds], setStatusIds],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'placements', 'types', 'statuses']);

  const {
    data: announcementsData,
    error,
    refetch,
    isPending,
  } = useQuery(
    getAnnouncements({
      pageIndex,
      pageSize,
      filters: {
        query: activeNameQuery,
        placements: activePlacementIds,
        types: activeTypeIds,
        statuses: activeStatusIds,
      },
    }),
  );

  const { announcements, total } = announcementsData || {};

  const columnHelper = createColumnHelper<Announcement>();

  const columns = [
    columnHelper.accessor('title', {
      id: 'title',
      header: 'Title',
      cell: ({ getValue }) => {
        return getValue() || '';
      },
    }),

    columnHelper.accessor('content', {
      id: 'content',
      header: 'Content',
      cell: ({ getValue }) => {
        return getValue();
      },
    }),

    columnHelper.accessor('placement', {
      id: 'placement',
      header: 'Placement',
      cell: ({ getValue }) => {
        return (
          <Badge variant="default" className="whitespace-nowrap">
            {AnnouncementPlacementLabelMap[getValue()]}
          </Badge>
        );
      },
    }),

    columnHelper.accessor('type', {
      id: 'type',
      header: 'Type',
      cell: ({ getValue }) => {
        return <AnnouncementTypeBadge type={getValue()} />;
      },
    }),

    columnHelper.accessor('status', {
      id: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue();
        const style =
          status === AnnouncementStatus.active ? 'border-green-200 text-green-800' : 'border-red-200 text-red-800';

        return (
          <Badge variant="outline" className={clsx(style)}>
            {AnnouncementStatusLabelMap[status]}
          </Badge>
        );
      },
    }),

    columnHelper.accessor((row) => row, {
      header: 'Last edit',
      cell: ({ getValue }) => {
        const { createdAt, updatedAt } = getValue();

        if (!createdAt && !updatedAt) return null;

        const { formattedDateTime } = formatInstant(Temporal.Instant.from(updatedAt || createdAt), timeZone, {
          hour12: true,
        });
        return formattedDateTime;
      },
    }),

    columnHelper.accessor((row) => row, {
      id: 'actions',
      header: '',
      cell: ({ getValue }) => (
        <>
          <AnnouncementFormModal
            trigger={
              <Button variant={'outline'} size={'sm'} title="Edit">
                <Pencil className="h-4 w-4" />
              </Button>
            }
            key={getValue().id}
            from={getValue()}
          />
        </>
      ),
    }),
  ];

  const table = useReactTable({
    data: announcements || [],
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    rowCount: total || 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    defaultColumn: {
      enableSorting: false,
    },
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search by title or content"
          value={nameQuery ?? ''}
          onChange={(event) => {
            setNameQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="Placement"
          options={announcementPlacementOptions}
          selectedValues={placementIds ?? []}
          onChange={setPlacementIds}
        />
        <TableFilterDropdown
          title="Type"
          options={announcementTypeOptions}
          selectedValues={typeIds ?? []}
          onChange={setTypeIds}
        />
        <TableFilterDropdown
          title="Status"
          options={announcementStatusOptions}
          selectedValues={statusIds ?? []}
          onChange={setStatusIds}
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      nameQuery,
      setNameQuery,
      placementIds,
      setPlacementIds,
      typeIds,
      setTypeIds,
      statusIds,
      setStatusIds,
      resetFilters,
    ],
  );

  return (
    <InfoPage
      headerText="Teacher Announcements"
      headerActions={<AnnouncementFormModal trigger={<Button>Add Announcement</Button>} key="create" />}
      tabs={coachesSectionTabs()}
    >
      <EntityTable
        table={table}
        isLoading={isPending}
        showPagination
        emptyState={'No announcements found'}
        hasError={!!error}
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents }}
      />
    </InfoPage>
  );
}
